import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useActiveWeb3React } from './index'
import { fetchCheckinHistory, fetchPointsOverview } from './pointsApi'

type ClaimableSnapshot = {
  nextClaimableAtServerMs: number | null
  serverTimeOffsetMs: number
  lastSnapshotAtMs: number
}

const STALE_SNAPSHOT_MS = 2 * 60 * 1000
const REVALIDATE_INTERVAL_MS = 30 * 1000

const getCurrentPeriod = () => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

export default function useDailyCheckinClaimable() {
  const { account } = useActiveWeb3React()
  const [isLoading, setIsLoading] = useState(false)
  const [snapshot, setSnapshot] = useState<ClaimableSnapshot | null>(null)
  const [tickNowMs, setTickNowMs] = useState(() => Date.now())
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!account) {
      if (mountedRef.current) {
        setSnapshot(null)
        setIsLoading(false)
      }
      return
    }
    if (mountedRef.current) setIsLoading(true)
    try {
      const overview = await fetchPointsOverview(account)
      const period = overview.data?.currentPeriod?.period ?? getCurrentPeriod()
      const history = await fetchCheckinHistory(account, period)
      const serverTimeMs =
        history.data?.serverTime != null && !Number.isNaN(new Date(history.data.serverTime).getTime())
          ? new Date(history.data.serverTime).getTime()
          : Date.now()
      const checkins = history.data?.checkinHistory ?? []
      const latestServerCreatedAt =
        checkins.length > 0 ? Math.max(...checkins.map((item) => new Date(item.createdAt).getTime())) : null
      const nextClaimableAtServerMs = latestServerCreatedAt != null ? latestServerCreatedAt + 24 * 60 * 60 * 1000 : null

      if (mountedRef.current) {
        setSnapshot({
          nextClaimableAtServerMs,
          serverTimeOffsetMs: serverTimeMs - Date.now(),
          lastSnapshotAtMs: Date.now(),
        })
      }
    } catch {
      // Keep existing snapshot if available; fail-safe stale guard will hide dot if too old.
    } finally {
      if (mountedRef.current) setIsLoading(false)
    }
  }, [account])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    const id = setInterval(() => setTickNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!account) return undefined
    const id = setInterval(() => {
      refresh()
    }, REVALIDATE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [account, refresh])

  useEffect(() => {
    if (!account) return undefined
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [account, refresh])

  const isClaimable = useMemo(() => {
    if (!account || !snapshot) return false
    if (tickNowMs - snapshot.lastSnapshotAtMs > STALE_SNAPSHOT_MS) return false
    if (snapshot.nextClaimableAtServerMs == null) return true
    const serverNowMs = tickNowMs + snapshot.serverTimeOffsetMs
    return serverNowMs >= snapshot.nextClaimableAtServerMs
  }, [account, snapshot, tickNowMs])

  return { isClaimable, isLoading, refresh }
}
